import { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'

export interface HeatPoint {
  x: number
  y: number
}

/** Blob radius as a share of the picture's width — about 2° of visual angle
 *  on a desktop screen, the usual fixation-map kernel. */
const RADIUS_OF_WIDTH = 0.045

/** Colour ramp of an eye-tracking heat map: cold where few looks landed,
 *  red where most did. Alpha rises with it so sparse areas stay see-through. */
const STOPS: Array<[number, [number, number, number, number]]> = [
  [0.0, [0, 0, 255, 0]],
  [0.15, [0, 90, 255, 120]],
  [0.35, [0, 220, 220, 160]],
  [0.55, [60, 220, 60, 180]],
  [0.75, [255, 230, 0, 200]],
  [1.0, [255, 30, 0, 220]],
]

let palette: Uint8ClampedArray | null = null
function getPalette(): Uint8ClampedArray {
  if (palette) return palette
  const lut = new Uint8ClampedArray(256 * 4)
  for (let i = 0; i < 256; i++) {
    const v = i / 255
    let j = 1
    while (j < STOPS.length - 1 && STOPS[j][0] < v) j++
    const [p0, c0] = STOPS[j - 1]
    const [p1, c1] = STOPS[j]
    const f = p1 > p0 ? Math.min(1, Math.max(0, (v - p0) / (p1 - p0))) : 0
    for (let k = 0; k < 4; k++) lut[i * 4 + k] = c0[k] + (c1[k] - c0[k]) * f
  }
  palette = lut
  return lut
}

/**
 * Gaze heat map over a picture (eye-tracking studies).
 *
 * Every gaze sample (`x`/`y` 0..1 on the picture) adds a soft blob to an
 * intensity layer; the layer is normalised to its own peak and coloured
 * blue → green → yellow → red. Fills its positioned parent; never takes clicks.
 */
export default function GazeHeatmap({ points, opacity = 0.75 }: { points: HeatPoint[]; opacity?: number }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })

  useEffect(() => {
    const el = canvasRef.current?.parentElement
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setSize({ w: Math.round(width), h: Math.round(height) })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || size.w === 0 || size.h === 0) return
    // Half resolution is plenty for blurred blobs and keeps redraws cheap
    // while the replay plays.
    const scale = 0.5
    const w = Math.max(1, Math.round(size.w * scale))
    const h = Math.max(1, Math.round(size.h * scale))
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, w, h)
    if (points.length === 0) return

    const r = Math.max(4, w * RADIUS_OF_WIDTH)
    // Few samples get a stronger blob each, so one short glance still shows.
    const alpha = Math.min(0.5, Math.max(0.04, 6 / Math.sqrt(points.length + 1) / 10))
    for (const p of points) {
      const cx = p.x * w
      const cy = p.y * h
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r)
      g.addColorStop(0, `rgba(0,0,0,${alpha})`)
      g.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = g
      ctx.fillRect(cx - r, cy - r, r * 2, r * 2)
    }

    const img = ctx.getImageData(0, 0, w, h)
    const data = img.data
    let peak = 0
    for (let i = 3; i < data.length; i += 4) if (data[i] > peak) peak = data[i]
    if (peak === 0) return
    const lut = getPalette()
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3]
      if (a === 0) continue
      const idx = Math.min(255, Math.round((a / peak) * 255)) * 4
      data[i] = lut[idx]
      data[i + 1] = lut[idx + 1]
      data[i + 2] = lut[idx + 2]
      data[i + 3] = lut[idx + 3]
    }
    ctx.putImageData(img, 0, 0)
  }, [points, size])

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      aria-hidden
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity }}
    />
  )
}
